import React, { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import MarkdownRenderer from "./MarkdownRenderer";
import DocumentViewerDialog from "../../Dialogs/DocumentViewerDialog";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";

interface Source {
  source: string;
  page: string;
  content_preview: string;
}

interface MessageMetadataProps {
  metadata: {
    fact_check?: string;
    quotes?: string;
    sources?: Source[];
    related_questions?: string[];
  };
  onRelatedQuestionClick: (question: string) => void;
}

const MessageMetadata: React.FC<MessageMetadataProps> = ({
  metadata,
  onRelatedQuestionClick,
}) => {
  const theme = useTheme();
  const [showFactCheck, setShowFactCheck] = useState<boolean>(false);
  const [showQuotes, setShowQuotes] = useState<boolean>(false);
  const [showSources, setShowSources] = useState<boolean>(false);
  // FIX: Change initial state to false to be collapsed by default
  const [showRelatedQuestions, setShowRelatedQuestions] =
    useState<boolean>(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    url: string;
    type: string;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const getFileType = (filename: string): string => {
    const extension = filename.split(".").pop();
    return extension ? extension.toUpperCase() : "";
  };

  const handleSourceDocumentClick = (source: string) => {
    try {
      const filename = source;
      if (!filename) {
        console.error("Invalid file path");
        return;
      }
      const decodedFilename = decodeURIComponent(filename);
      const baseUrl = `https://copilotdms.blob.core.windows.net/copilotdms/`;
      const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;

      if (!sasToken) {
        console.error("Azure Storage SAS token is not configured");
        return;
      }

      const fullUrl = `${baseUrl}${encodeURIComponent(
        decodedFilename,
      )}${sasToken}`;
      const fileType = getFileType(decodedFilename);

      setSelectedDocument({
        name: decodedFilename,
        url: fullUrl,
        type: fileType,
      });
      setViewerOpen(true);
    } catch (err) {
      console.error("Error opening document:", err);
      setSnackbar({
        open: true,
        message: "Error viewing document. Please try again.",
        severity: "error",
      });
    }
  };

  if (!metadata || Object.keys(metadata).length === 0) {
    return null;
  }

  const glassContainerSx = {
    background: theme.palette.mode === 'dark' 
        ? 'rgba(0, 0, 0, 0.25)' 
        : 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    borderRadius: '16px',
    overflow: 'hidden',
  };

  const headerSx = {
    p: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.05)
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* {metadata.fact_check && (
        <Box sx={glassContainerSx}>
          <Box sx={headerSx} onClick={() => setShowFactCheck(!showFactCheck)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>ตรวจสอบความถูกต้อง</Typography>
            </Box>
            <IconButton size="small" edge="end">
              {showFactCheck ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showFactCheck} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <MarkdownRenderer content={metadata.fact_check} />
            </Box>
          </Collapse>
        </Box>
      )} */}

      {metadata.quotes && metadata.quotes !== "INPUT SOURCE DOES NOT MATCH" && (
        <Box sx={glassContainerSx}>
          <Box sx={headerSx} onClick={() => setShowQuotes(!showQuotes)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SubjectOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>เนื้อหาที่เกี่ยวข้อง</Typography>
            </Box>
            <IconButton size="small" edge="end">
              {showQuotes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showQuotes} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <MarkdownRenderer content={metadata.quotes} />
            </Box>
          </Collapse>
        </Box>
      )}

      {metadata.sources && metadata.sources.length > 0 && (
         <Box sx={glassContainerSx}>
          <Box sx={headerSx} onClick={() => setShowSources(!showSources)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                อ้างอิง ({metadata.sources.length})
              </Typography>
            </Box>
            <IconButton size="small" edge="end">
              {showSources ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showSources} timeout="auto" unmountOnExit>
            <List disablePadding>
              {metadata.sources.map((source, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    py: 2,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {source.source.split("/").pop()}
                          </Typography>
                          {source.page && (
                              <Chip
                              label={`Page ${source.page}`}
                              size="small"
                              sx={{ mt: 0.5, fontSize: '10px', color: "white" }}
                              
                              />
                          )}
                      </Box>
                      
                      <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSourceDocumentClick(source.source);
                    }}
                    startIcon={<DescriptionOutlinedIcon />}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#fff',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(6px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '12px',
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    ดูเอกสาร
                  </Button>
                  </Box>
                  
                  {source.content_preview && (
                      <Typography variant="body2" component="div" sx={{ color: "white", mt: 1.5, width: '100%' }}>
                          <MarkdownRenderer content={`> ${source.content_preview}`} />
                      </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      {metadata.related_questions && metadata.related_questions.length > 0 && (
        <Box sx={glassContainerSx}>
          <Box sx={headerSx} onClick={() => setShowRelatedQuestions(!showRelatedQuestions)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                แนะนำคำถามที่เกี่ยวข้อง
              </Typography>
            </Box>
            <IconButton size="small" edge="end">
              {showRelatedQuestions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showRelatedQuestions} timeout="auto" unmountOnExit>
            <List disablePadding>
              {metadata.related_questions.map((question, index) =>
                question ? (
                  <ListItem
                    key={index}
                    onClick={() => onRelatedQuestionClick(question)}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                      },
                      cursor: "pointer",
                    }}
                  >
                    <ListItemText primary={<Typography variant="body2">{question}</Typography>} />
                  </ListItem>
                ) : null,
              )}
            </List>
          </Collapse>
        </Box>
      )}

      <DocumentViewerDialog
        viewerOpen={viewerOpen}
        setViewerOpen={setViewerOpen}
        name={selectedDocument?.name}
        type={selectedDocument?.type}
        url={selectedDocument?.url}
        theme={theme}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageMetadata;